import { SortCriterion } from '../components/UI/Sorter';
import { ComprobantePagoRes as Comprobante } from '../types/Reintegro/types';
import { TipoSiniestro } from '../types/Siniestro/types';
import { capitalizeFirstLetter } from './string';

// Se incorpora sanitización de caracteres en la descripción del siniestro,
// limitando el input a letras, números y puntuación básica, además de normalizar whitespace.
const sanitizeDenuncia = (input?: string): string => {
  if (!input) return '';

  return input
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ.,;:()¿?¡!'"/\-\s]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

const formatDenunciaByTipo = (tipo: TipoSiniestro, denuncia: string): string => {
  if (tipo === 'vacuna') {
    return `VACUNA: ${denuncia}`;
  }

  return denuncia;
};

type CleanDenunciaParams = {
  denuncia: string;
  tipo?: TipoSiniestro;
  isVacuna?: boolean;
};

const cleanDenunciaByTipo = ({
  denuncia,
  tipo,
  isVacuna,
}: CleanDenunciaParams): string => {
  const shouldClean = isVacuna ?? tipo === 'vacuna';

  if (shouldClean) {
    return denuncia.replace(/^VACUNA:\s*/i, '');
  }

  return denuncia;
};

const truncateDenuncia = (text: string, max = 25) => {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '...' : text;
};

const getColorByEstado = (estado?: string): string => {
  if (!estado) return 'medium';

  const normalized = estado.toLowerCase();

  if (normalized.includes('pend')) return 'danger';
  if (normalized.includes('env')) return 'warning';
  if (normalized.includes('comp')) return 'success';
  if (normalized.includes('liqui')) return 'success';

  return 'medium';
};

const formatTipo = (tipo: string): string => {
  const key = tipo.trim().toLowerCase();

  let result = tipo;

  switch (key) {
    case 'consulta':
      result = 'Consulta';
      break;

    case 'estudiomascota':
      result = 'Estudio';
      break;

    case 'practicamascota':
      result = 'Practica';
      break;

    case 'cirugiaMascota':
      result = 'Cirugía';
      break;

    case 'internacionMascota':
      result = 'Internación';
      break;

    case 'farmacologiamascota':
      result = 'Farmacología';
      break;

    default:
      result = tipo;
  }

  return capitalizeFirstLetter(result);
};

const safeCompare = (a?: string | null, b?: string | null) => {
  return (a ?? '').localeCompare(b ?? '');
};

const siniestros_sortCriteria: SortCriterion<any>[] = [
  {
    key: 'recent',
    label: 'Fecha más reciente',
    compare: (a, b) =>
      new Date(b.fecha_carga).getTime() - new Date(a.fecha_carga).getTime(),
  },
  {
    key: 'oldest',
    label: 'Fecha más antigua',
    compare: (a, b) =>
      new Date(a.fecha_carga).getTime() - new Date(b.fecha_carga).getTime(),
  },
  {
    key: 'title_asc',
    label: 'Título A → Z',
    compare: (a, b) => safeCompare(a.denuncia_administrativa, b.denuncia_administrativa),
  },
  {
    key: 'title_desc',
    label: 'Título Z → A',
    compare: (a, b) => safeCompare(b.denuncia_administrativa, a.denuncia_administrativa),
  },
  {
    key: 'estado',
    label: 'Estado',
    compare: (a, b) => safeCompare(a.estado, b.estado),
  },
  {
    key: 'tipo',
    label: 'Tipo',
    compare: (a, b) => safeCompare(a.tipo, b.tipo),
  },
];

const getDescripcion = (svf) => {
  return svf.tipoEstudio || svf.tipoPractica || 'Sin detalles';
};

const getComprobanteMontoKey = (comprobante: Comprobante): string => {
  const explicitId =
    comprobante.comprobanteId
    ?? comprobante.comprobante_id
    ?? comprobante.comprobanteClienteId
    ?? comprobante.comprobante_cliente_id
    ?? comprobante.comprobante?.id
    ?? comprobante.comprobanteCliente?.id;

  if (explicitId !== null && explicitId !== undefined && explicitId !== '') {
    return `id-${explicitId}`;
  }

  const concepto = comprobante.concepto?.trim().toLowerCase() ?? '';

  if (!concepto) return `file-${comprobante.file}`;

  return `${concepto}-${Number(comprobante.monto)}`;
};

const getComprobantesStats = (comprobantes?: Comprobante[]) => {
  const validComprobantes = comprobantes?.filter((c) => Boolean(c.file)) ?? [];
  const countedMontoKeys = new Set<string>();

  const totalMonto = validComprobantes.reduce((acc, c) => {
    const monto = Number(c.monto);
    // Si monto es NaN o no es un número válido, usar 0
    if (isNaN(monto) || monto <= 0) return acc;

    const montoKey = getComprobanteMontoKey(c);

    if (countedMontoKeys.has(montoKey)) return acc;

    countedMontoKeys.add(montoKey);
    return acc + monto;
  }, 0);

  return {
    validComprobantes,
    cantidad: validComprobantes.length,
    totalMonto,
  };
};

export {
  sanitizeDenuncia,
  formatDenunciaByTipo,
  cleanDenunciaByTipo,
  truncateDenuncia,
  getColorByEstado,
  formatTipo,
  safeCompare,
  siniestros_sortCriteria,
  getDescripcion,
  getComprobantesStats,
};
